import { useEffect, useState } from "react";

/**
 * Custom React hook that determines if a permission-denied (HTTP 403) error has occurred.
 *
 * This hook listens to error state changes from requests and checks if the error is an HTTP 403 (forbidden).
 * If so, it sets `permissionDenied` to true. Otherwise, it resets to false on cleanup or other errors.
 *
 * @param {boolean} isError - Boolean indicating if an error occurred during a request.
 * @param {Object} error - The error object returned from the request, expected to have a `response` field with a `status`.
 * @returns {boolean} permissionDenied - Returns true if the error status is 403 (permission denied), otherwise false.
 *
 * @example
 * const permissionDenied = usePermissionDenied(isError, error);
 * if (permissionDenied) {
 *   // Show permission denied message/UI
 * }
 */
const usePermissionDenied = (isError, error) => {
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    if (isError) {
      if (error?.response?.status === 403) {
        setPermissionDenied(true);
      }
    }
    return () => {
      setPermissionDenied(false);
    };
  }, [isError, error]);

  return permissionDenied;
};

export default usePermissionDenied;
